export interface AudioDevice {
  deviceId: string;
  label: string;
  kind: "audioinput" | "audiooutput";
}

export interface AudioSettings {
  selectedInputDevice: AudioDevice | null;
  hasConfiguredDevice: boolean;
  lastConfiguredAt: string | null;
}

const AUDIO_SETTINGS_KEY = "cohesive-audio-settings";

export const audioSettingsStorage = {
  get(): AudioSettings {
    try {
      const stored = localStorage.getItem(AUDIO_SETTINGS_KEY);
      if (!stored) {
        return {
          selectedInputDevice: null,
          hasConfiguredDevice: false,
          lastConfiguredAt: null,
        };
      }
      return JSON.parse(stored);
    } catch (error) {
      console.error("Failed to parse audio settings from localStorage:", error);
      return {
        selectedInputDevice: null,
        hasConfiguredDevice: false,
        lastConfiguredAt: null,
      };
    }
  },

  set(settings: Partial<AudioSettings>): void {
    try {
      const current = this.get();
      const updated = {
        ...current,
        ...settings,
        lastConfiguredAt: new Date().toISOString(),
      };
      localStorage.setItem(AUDIO_SETTINGS_KEY, JSON.stringify(updated));
    } catch (error) {
      console.error("Failed to save audio settings to localStorage:", error);
    }
  },

  clear(): void {
    try {
      localStorage.removeItem(AUDIO_SETTINGS_KEY);
    } catch (error) {
      console.error("Failed to clear audio settings from localStorage:", error);
    }
  },

  setSelectedDevice(device: AudioDevice): void {
    this.set({
      selectedInputDevice: device,
      hasConfiguredDevice: true,
    });
  },

  hasConfiguredDevice(): boolean {
    const settings = this.get();
    return (
      settings.hasConfiguredDevice && settings.selectedInputDevice !== null
    );
  },

  getSelectedDevice(): AudioDevice | null {
    const settings = this.get();
    return settings.selectedInputDevice;
  },
};

export const getAudioInputDevices = async (): Promise<AudioDevice[]> => {
  try {
    // First request permission if not already granted
    await navigator.mediaDevices.getUserMedia({ audio: true });

    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
      .filter((device) => device.kind === "audioinput")
      .map((device) => ({
        deviceId: device.deviceId,
        label: device.label || `Microphone ${device.deviceId.slice(0, 8)}`,
        kind: "audioinput" as const,
      }));
  } catch (error) {
    console.error("Failed to get audio input devices:", error);
    throw new Error(
      "Unable to access audio devices. Please check your browser permissions.",
    );
  }
};

export const testAudioDevice = async (deviceId: string): Promise<boolean> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { deviceId: { exact: deviceId } },
    });

    // Test if the stream has audio tracks
    const audioTracks = stream.getAudioTracks();
    const isWorking =
      audioTracks.length > 0 && audioTracks[0].readyState === "live";

    // Clean up
    stream.getTracks().forEach((track) => track.stop());

    return isWorking;
  } catch (error) {
    console.error("Failed to test audio device:", error);
    return false;
  }
};
